/**
  Season ticket holder waiting list component
 */
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { HolderModel } from '../models/holder.model';
import { FullLoader } from './loader.component';
import styles from './season-ticket-summary.module.scss';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { CmsConfigs } from '../models/configs.model';
import { getConfigs, getHomepath } from '../util/cms-utils';
import { HolderService, HOLDER_STATUS_NOT_ENTITLED, HOLDER_STATUS_VERIFY_EMAIL } from '../services/holder.service';

const premiumPlanToPath = [
  { regex: /^Suite Full$/, path: 'benefits-full' },
  { regex: /^Suite 40(A|B)$/, path: 'benefits-40a' },
  { regex: /^Suite 20[A-F]$/, path: 'benefits-20-plan' },
  { regex: /^Suite Pick 10$/, path: 'benefits-10-games' },
  { regex: /^Suite 10 Package [1-8]$/, path: 'benefits-10-games' },
]

const initialHolder: HolderModel = {
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  phoneNumberPrimary: '',
  phoneNumberSecondary: '',
  dateOfBirth: ['', '', ''],
  learnMore: [],
  email: '',
  clubCode: '',
  venueId: '',
  waitingListNumber: '',
  ticketHolderSalesReps: [],
  cubsSms: false,
  cubsAlertsSms: false,
  tenure: '',
};

const SeasonTicketSummary = ({ holderHost }: { holderHost: string; }) => {
  const { authState } = useOktaAuth()
  const [holder, setHolder] = useState<HolderModel>(initialHolder);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<CmsConfigs>();
  const { search } = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(search);
    const config: CmsConfigs = getConfigs(query.get('clubCode'));

    async function init() {
      try {
        setShowLoading(true);
        setConfig(config);
        if (!config.features.includes('partner')) {
          // Club don't have partner function
          window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
        }
        const holderService: HolderService = new HolderService(authState, holderHost);
        const result: HolderModel = await holderService.getValidationStatus(config.clubCode, config.venueId);
        if (result && result.email) {
          setHolder(result);
        } else {
          window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
          return;
        }
      } catch (_error) {
        const error = _error as Error
        if (error.message === HOLDER_STATUS_VERIFY_EMAIL) {
          // app show email verify button
          window.parent.location.href = `${getHomepath(config)}/app`;
        } else if (error.message === HOLDER_STATUS_NOT_ENTITLED) {
          window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
        }
      } finally {
        setShowLoading(false);
      }
    };
    init();
  }, [authState, holderHost, search]);

  const {
    accountId,
    firstName,
    lastName,
    planNames,
    premiumPlan,
    tenure,
    ticketHolderSalesReps,
  } = holder;

  const planNameForDisplay = premiumPlan || planNames
  const premiumPlanPath = premiumPlan && premiumPlanToPath.find(({ regex }) => regex.test(premiumPlan))?.path;
  const tenureForDisplay = tenure?.split('STM Tenure - ')?.pop();

  return (
    <div className={classNames(styles.box, showLoading && styles.minHeight)}>
      {showLoading ? (
        <FullLoader />
      ) : (
        <>
          <p>Welcome, {firstName} {lastName}.</p>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Account #:</span>
            <span className={styles.rowValue}>{accountId}</span>
          </div>
          {tenureForDisplay && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>MyTwins Member Tenure:</span>
              <span className={styles.rowValue}>{tenureForDisplay}</span>
            </div>
          )}
          {planNameForDisplay && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Plan Type:</span>
              <span className={styles.rowValue}>
                {planNameForDisplay.split(',').map(planName => <div key={planName}>{planName.trim()}</div>)}
              </span>
            </div>
          )}
          {premiumPlanPath && (
            <a href={`${[getHomepath(config), 'suite-services', premiumPlanPath].join('/')}`} className={styles.premiumLink} target='_parent'>Access Your Suite Holder Benefits</a>
          )}
          {(ticketHolderSalesReps && ticketHolderSalesReps.length > 0) && (
            <div className={styles.accountInfo}>
              <div className={styles.salesRepInfoTitle}>
                Have a question about your season ticket account? Contact your Twins Ticket Sales & Service Rep:
              </div>
              {ticketHolderSalesReps.map(salesRep => {
                const {
                  firstName,
                  lastName,
                  phoneNumber,
                  email,
                } = salesRep;

                return (
                  <div className={styles.salesRepInfoValue}>
                      <p>{firstName} {lastName}</p>
                      <p>{phoneNumber}</p>
                      <p><a href={`mailto:${email}`}>{email}</a></p>
                  </div>
                );
               })
              }
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SeasonTicketSummary;
